import type { FC } from "react";
import React, { useState } from "react";

import { hasValue } from "@core/foundation/types";

import { Button } from "@react/ui-primitives/button";

import type { MemoryApiKey, Project } from "@apis/memory/models";

import { useMemoryActions } from "../hooks/useMemoryActions.ts";

import { MemoryApiKeyDialog } from "./MemoryApiKeyDialog.tsx";

interface MemoryApiKeySectionProps {
  readonly project: Project;
}

export const MemoryApiKeySection: FC<MemoryApiKeySectionProps> = ({
  project,
}) => {
  const [memoryApiCredential, setMemoryApiCredential] = useState<null | string>(
    null,
  );

  const [memoryApiKey, setMemoryApiKey] = useState<MemoryApiKey | null>(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { generateMemoryApiKey } = useMemoryActions();

  const hasKey = hasValue(project.apiKeyHash);

  const generate = (): void => {
    generateMemoryApiKey.mutate(
      { projectId: project.id },
      {
        onSuccess: response => {
          if (hasValue(response)) {
            setMemoryApiCredential(response.memoryApiCredential);
            setMemoryApiKey(response.memoryApiKey);
            setIsDialogOpen(true);
          }
        },
      },
    );
  };

  return (
    <>
      <div className="flex items-center justify-between rounded-md border p-3">
        <span className="text-sm">
          {hasKey
            ? "A key is set. Generating another key replaces it."
            : "No key yet. Generate a key to connect clients."}
        </span>
        <Button
          disabled={generateMemoryApiKey.isPending}
          onClick={generate}
          variant="outline"
        >
          {hasKey ? "Regenerate key" : "Generate key"}
        </Button>
      </div>
      <MemoryApiKeyDialog
        memoryApiCredential={memoryApiCredential}
        memoryApiKey={memoryApiKey}
        onOpenChange={setIsDialogOpen}
        open={isDialogOpen}
      />
    </>
  );
};
